import { useState } from 'react';
import {
  CheckCircle2,
  XCircle,
  ChevronRight,
  ChevronDown,
  AlertTriangle,
  Link2,
  CircleDot,
  FileText,
  Phone,
  Mail,
  ScanSearch,
} from 'lucide-react';
import { getScoreColor } from '@/lib/utils';

// ============================================
// PageDetailCard — SEO Quality (unchanged)
// ============================================

// Helper to get icon color based on score
function getScoreIconColor(score: number | undefined, hasValue: boolean): string {
  if (!hasValue) return 'text-red-500';
  if (score === undefined) return 'text-green-500';
  if (score >= 90) return 'text-green-500';
  if (score >= 70) return 'text-orange-400';
  return 'text-red-500';
}

export function PageDetailCard({ page }: { page: any }) {
  const [expanded, setExpanded] = useState(false);
  const qualityAnalysis = page.qualityAnalysis;

  const overallScore = qualityAnalysis?.overallScore ?? 0;
  const titleScore = qualityAnalysis?.title?.score;
  const descScore = qualityAnalysis?.description?.score;
  const headingsScore = qualityAnalysis?.headings?.score;

  // Determine icon colors based on scores
  const titleIconColor = getScoreIconColor(titleScore, !!page.title);
  const descIconColor = getScoreIconColor(descScore, !!page.metaDescription);
  const headingsIconColor = getScoreIconColor(headingsScore, page.h1Tags.length === 1);

  return (
    <div className="bg-secondary/30 rounded-lg border border-border/50 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-secondary/50 transition-colors"
      >
        <div className="flex items-center gap-4">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <div className="text-left">
            <p className="font-medium">{page.humanReadableName}</p>
            <p className="text-xs text-muted-foreground font-mono">{page.path}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-xs text-muted-foreground">SEO</p>
            <p className={`text-sm font-medium ${getScoreColor(overallScore)}`}>{overallScore}</p>
          </div>
          <div className="flex gap-1">
            {page.title ? <CheckCircle2 className={`h-4 w-4 ${titleIconColor}`} /> : <XCircle className="h-4 w-4 text-red-500" />}
            {page.metaDescription ? <CheckCircle2 className={`h-4 w-4 ${descIconColor}`} /> : <XCircle className="h-4 w-4 text-red-500" />}
            {page.h1Tags.length === 1 ? <CheckCircle2 className={`h-4 w-4 ${headingsIconColor}`} /> : <XCircle className="h-4 w-4 text-red-500" />}
          </div>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-border/50 divide-y divide-border/30">
          {/* Title Tag */}
          <div className="px-4 py-3 space-y-1.5">
            <div className="flex items-center gap-2">
              {page.title
                ? <CheckCircle2 className={`h-3.5 w-3.5 ${titleIconColor} shrink-0`} />
                : <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />}
              <span className="text-sm font-medium">Title Tag</span>
              <span className="text-xs text-muted-foreground/60">{page.title?.length || 0} chars</span>
              <span className="flex-1" />
              {qualityAnalysis?.title && (
                <span className={`text-xs font-medium tabular-nums ${getScoreColor(qualityAnalysis.title.score)}`}>{qualityAnalysis.title.score}</span>
              )}
            </div>
            <p className="text-xs text-foreground/70 pl-[22px] leading-relaxed">
              {page.title || <span className="text-red-400 italic">Missing title tag</span>}
            </p>
            {qualityAnalysis?.title?.issues?.length > 0 && (
              <div className="pl-[22px] space-y-0.5">
                {qualityAnalysis.title.issues.map((issue: string, i: number) => (
                  <p key={i} className="text-xs text-orange-400/80 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3 shrink-0" /> {issue}
                  </p>
                ))}
              </div>
            )}
          </div>

          {/* Meta Description */}
          <div className="px-4 py-3 space-y-1.5">
            <div className="flex items-center gap-2">
              {page.metaDescription
                ? <CheckCircle2 className={`h-3.5 w-3.5 ${descIconColor} shrink-0`} />
                : <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />}
              <span className="text-sm font-medium">Meta Description</span>
              <span className="text-xs text-muted-foreground/60">{page.metaDescription?.length || 0} chars</span>
              <span className="flex-1" />
              {qualityAnalysis?.description && (
                <span className={`text-xs font-medium tabular-nums ${getScoreColor(qualityAnalysis.description.score)}`}>{qualityAnalysis.description.score}</span>
              )}
            </div>
            <p className="text-xs text-foreground/70 pl-[22px] leading-relaxed">
              {page.metaDescription || <span className="text-red-400 italic">Missing meta description</span>}
            </p>
            {qualityAnalysis?.description?.issues?.length > 0 && (
              <div className="pl-[22px] space-y-0.5">
                {qualityAnalysis.description.issues.map((issue: string, i: number) => (
                  <p key={i} className="text-xs text-orange-400/80 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3 shrink-0" /> {issue}
                  </p>
                ))}
              </div>
            )}
          </div>

          {/* Heading Structure */}
          <div className="px-4 py-3 space-y-1.5">
            <div className="flex items-center gap-2">
              {page.h1Tags.length === 1
                ? <CheckCircle2 className={`h-3.5 w-3.5 ${headingsIconColor} shrink-0`} />
                : <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />}
              <span className="text-sm font-medium">Headings</span>
              <span className="text-xs text-muted-foreground/60">{page.h1Tags.length} H1 · {page.h2Tags.length} H2</span>
              <span className="flex-1" />
              {qualityAnalysis?.headings && (
                <span className={`text-xs font-medium tabular-nums ${getScoreColor(qualityAnalysis.headings.score)}`}>{qualityAnalysis.headings.score}</span>
              )}
            </div>
            <div className="pl-[22px] space-y-1">
              {page.h1Tags.length > 0 ? (
                page.h1Tags.map((h1: string, i: number) => (
                  <p key={i} className="text-xs text-foreground/70 leading-relaxed">{h1}</p>
                ))
              ) : (
                <p className="text-xs text-red-400 italic">No H1 tag found</p>
              )}
              {page.h2Tags.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-0.5">
                  {page.h2Tags.slice(0, 6).map((h2: string, i: number) => (
                    <span key={i} className="text-[11px] text-muted-foreground bg-secondary/60 px-1.5 py-0.5 rounded">
                      {h2.slice(0, 28)}{h2.length > 28 ? '...' : ''}
                    </span>
                  ))}
                  {page.h2Tags.length > 6 && (
                    <span className="text-[11px] text-muted-foreground/60">+{page.h2Tags.length - 6} more</span>
                  )}
                </div>
              )}
            </div>
            {qualityAnalysis?.headings?.issues?.length > 0 && (
              <div className="pl-[22px] space-y-0.5">
                {qualityAnalysis.headings.issues.map((issue: string, i: number) => (
                  <p key={i} className="text-xs text-orange-400/80 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3 shrink-0" /> {issue}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// Element Tracking — Type icons & helpers
// ============================================

const TYPE_ICONS: Record<string, React.ReactNode> = {
  link: <Link2 className="h-3.5 w-3.5" />,
  button: <CircleDot className="h-3.5 w-3.5" />,
  form: <FileText className="h-3.5 w-3.5" />,
  phone: <Phone className="h-3.5 w-3.5" />,
  email: <Mail className="h-3.5 w-3.5" />,
};

const TRACKING_METHOD_LABELS: Record<string, string> = {
  'enhanced-measurement': 'GA4 Enhanced Measurement',
  'gtm-event': 'GTM Configured Events',
  'gtm-click-trigger': 'GTM Click Triggers',
  'call-tracking': 'Google Call Tracking',
  'inline': 'Inline Code',
};

function getMethodColor(method: string): string {
  switch (method) {
    case 'enhanced-measurement': return 'bg-blue-500';
    case 'gtm-event': return 'bg-purple-500';
    case 'gtm-click-trigger': return 'bg-indigo-500';
    case 'call-tracking': return 'bg-green-500';
    case 'inline': return 'bg-yellow-500';
    default: return 'bg-muted-foreground';
  }
}

// ============================================
// ElementTrackingOverview — Coverage summary
// ============================================

export function ElementTrackingOverview({ pages }: { pages: any[] }) {
  const totalElements = pages.reduce((sum: number, p: any) => sum + (p.trackingAnalysis?.elements?.length || 0), 0);
  const trackedCount = pages.reduce((sum: number, p: any) => sum + (p.trackingAnalysis?.trackedCount || 0), 0);
  const untrackedCount = totalElements - trackedCount;
  const criticalCount = pages.reduce((sum: number, p: any) => sum + (p.trackingAnalysis?.criticalUntrackedCount || 0), 0);
  const coverage = totalElements > 0 ? Math.round((trackedCount / totalElements) * 100) : 0;

  const summaryText = totalElements === 0
    ? 'No trackable elements found across crawled pages.'
    : coverage === 100
      ? `All ${totalElements} trackable elements across ${pages.length} pages are being tracked.`
      : `${totalElements} trackable elements found across ${pages.length} pages. ${coverage}% are tracked via GTM, GA4, or inline code.`;

  return (
    <div className="rounded-xl border border-border/60 overflow-hidden">
      <div className="bg-secondary/20 px-6 py-5">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-secondary/40 rounded-lg text-muted-foreground shrink-0 mt-0.5">
            <ScanSearch className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Element Tracking Coverage
            </span>
            <p className="text-base font-medium text-foreground leading-relaxed mt-1">
              {summaryText}
            </p>
          </div>
        </div>
      </div>

      <div className="px-6 py-4 space-y-3">
        {/* Progress bar */}
        <div className="space-y-1.5">
          <div className="w-full bg-secondary rounded-full h-2.5">
            <div
              className={`h-2.5 rounded-full transition-all ${
                coverage === 100 ? 'bg-green-500' :
                coverage >= 70 ? 'bg-green-500/80' :
                coverage >= 50 ? 'bg-yellow-500' :
                'bg-red-500'
              }`}
              style={{ width: `${coverage}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{coverage}% coverage</span>
            <span>{trackedCount} of {totalElements} elements</span>
          </div>
        </div>

        {/* Inline stats */}
        <div className="flex items-center gap-6 text-sm">
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-green-500" />
            <span className="text-muted-foreground">{trackedCount} tracked</span>
          </span>
          {untrackedCount > 0 && (
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-red-500" />
              <span className="text-muted-foreground">{untrackedCount} untracked</span>
            </span>
          )}
          {criticalCount > 0 && (
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-red-400 font-medium">{criticalCount} critical</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// TrackingMethodsChart — How elements are tracked
// ============================================

export function TrackingMethodsChart({ pages }: { pages: any[] }) {
  const methods: Record<string, number> = {};
  let total = 0;

  for (const page of pages) {
    if (!page.trackingAnalysis?.elements) continue;
    for (const el of page.trackingAnalysis.elements) {
      if (el.hasTracking && el.trackingMethod) {
        methods[el.trackingMethod] = (methods[el.trackingMethod] || 0) + 1;
        total++;
      }
    }
  }

  if (total === 0) return null;

  // Sort by count descending
  const sorted = Object.entries(methods).sort((a, b) => b[1] - a[1]);

  return (
    <div className="rounded-xl border border-border/60 overflow-hidden">
      <div className="px-6 py-4">
        <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-4">
          How Elements Are Tracked
        </h3>
        <div className="space-y-3">
          {sorted.map(([method, count]) => {
            const pct = Math.round((count / total) * 100);
            return (
              <div key={method} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-foreground">{TRACKING_METHOD_LABELS[method] || method}</span>
                  <span className="text-muted-foreground tabular-nums">{count} <span className="text-muted-foreground/50">({pct}%)</span></span>
                </div>
                <div className="w-full bg-secondary rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full ${getMethodColor(method)}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ============================================
// UntrackedElementsSummary — Priority-first action items
// ============================================

const PRIORITY_CONFIG: Record<string, { label: string; color: string; bgColor: string; icon: React.ReactNode }> = {
  critical: {
    label: 'Critical',
    color: 'text-red-400',
    bgColor: 'bg-red-500/10 border-red-500/30',
    icon: <XCircle className="h-4 w-4 text-red-500" />,
  },
  high: {
    label: 'High Priority',
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/10 border-orange-500/30',
    icon: <AlertTriangle className="h-4 w-4 text-orange-500" />,
  },
  medium: {
    label: 'Medium',
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/10 border-yellow-500/30',
    icon: <AlertTriangle className="h-4 w-4 text-yellow-500" />,
  },
  low: {
    label: 'Low',
    color: 'text-muted-foreground',
    bgColor: 'bg-secondary/30 border-border/50',
    icon: <CircleDot className="h-4 w-4 text-muted-foreground" />,
  },
};

interface UntrackedGroup {
  text: string;
  type: string;
  category: string;
  importance: string;
  pages: string[];
}

export function UntrackedElementsSummary({ pages }: { pages: any[] }) {
  // Deduplicate untracked elements across pages, tracking which pages each appears on
  const groupMap = new Map<string, UntrackedGroup>();

  for (const page of pages) {
    if (!page.trackingAnalysis?.elements) continue;
    for (const el of page.trackingAnalysis.elements) {
      if (el.hasTracking) continue;

      // Key by text + type + category for deduplication
      const key = `${el.type}:${el.text}:${el.category}`;
      const existing = groupMap.get(key);
      if (existing) {
        if (!existing.pages.includes(page.path)) {
          existing.pages.push(page.path);
        }
      } else {
        groupMap.set(key, {
          text: el.text || el.href || 'Unknown',
          type: el.type,
          category: el.category,
          importance: el.importance,
          pages: [page.path],
        });
      }
    }
  }

  const groups = Array.from(groupMap.values());
  if (groups.length === 0) return null;

  // Sort by priority
  const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
  groups.sort((a, b) => (priorityOrder[a.importance as keyof typeof priorityOrder] ?? 3) - (priorityOrder[b.importance as keyof typeof priorityOrder] ?? 3));

  return (
    <div className="rounded-xl border border-border/60 overflow-hidden">
      <div className="px-6 py-4">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="h-4 w-4 text-orange-500" />
          <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            {groups.length} Untracked Element{groups.length !== 1 ? 's' : ''} Need{groups.length === 1 ? 's' : ''} Attention
          </h3>
        </div>

        <div className="space-y-2">
          {groups.map((group, i) => {
            const config = PRIORITY_CONFIG[group.importance] || PRIORITY_CONFIG.low;
            return (
              <div key={i} className={`rounded-lg border p-3 ${config.bgColor}`}>
                <div className="flex items-start gap-3">
                  <div className="shrink-0 mt-0.5">{config.icon}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-muted-foreground shrink-0">{TYPE_ICONS[group.type]}</span>
                      <span className="text-sm font-medium truncate">{group.text}</span>
                      <span className="text-xs text-muted-foreground px-2 py-0.5 bg-secondary rounded shrink-0">{group.category}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {group.pages.length === 1
                        ? `Page: ${group.pages[0]}`
                        : `${group.pages.length} pages: ${group.pages.slice(0, 3).join(', ')}${group.pages.length > 3 ? ` +${group.pages.length - 3} more` : ''}`
                      }
                    </p>
                  </div>
                  <span className={`text-xs font-medium shrink-0 ${config.color}`}>{config.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ============================================
// ElementRow — Compact inline element display
// ============================================

function ElementRow({ element }: { element: any }) {
  return (
    <div className="flex items-center gap-3 py-2 px-4 text-sm group">
      <span className="text-muted-foreground shrink-0">
        {TYPE_ICONS[element.type] || <CircleDot className="h-3.5 w-3.5" />}
      </span>
      <span className="flex-1 truncate text-foreground/80">{element.text || element.href || 'Unknown'}</span>
      <span className="text-xs text-muted-foreground/60 px-2 py-0.5 bg-secondary/50 rounded shrink-0 hidden sm:block">
        {element.category}
      </span>
      <span className="flex items-center gap-1.5 shrink-0">
        <span className={`h-2 w-2 rounded-full ${element.hasTracking ? 'bg-green-500' : 'bg-red-500'}`} />
        <span className={`text-xs ${element.hasTracking ? 'text-muted-foreground' : 'text-red-400'}`}>
          {element.hasTracking ? (element.trackingType || 'Tracked') : 'Not tracked'}
        </span>
      </span>
    </div>
  );
}

// ============================================
// PageTrackingCard — Simplified collapsible page card
// ============================================

export function PageTrackingCard({ page }: { page: any }) {
  const [expanded, setExpanded] = useState(false);
  const trackingAnalysis = page.trackingAnalysis;

  if (!trackingAnalysis || !trackingAnalysis.elements?.length) {
    return null;
  }

  const trackingScore = trackingAnalysis.coverageScore ?? 0;
  const total = trackingAnalysis.elements.length;
  const tracked = trackingAnalysis.trackedCount;

  return (
    <div className="rounded-lg border border-border/50 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-secondary/30 transition-colors"
      >
        <span className="text-muted-foreground shrink-0">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </span>

        <div className="flex-1 min-w-0 text-left">
          <span className="text-sm font-medium">{page.humanReadableName}</span>
          <span className="text-xs text-muted-foreground ml-2 font-mono">{page.path}</span>
        </div>

        {/* Mini coverage bar */}
        <div className="flex items-center gap-3 shrink-0">
          <div className="w-16 bg-secondary rounded-full h-1.5 hidden sm:block">
            <div
              className={`h-1.5 rounded-full ${
                trackingScore === 100 ? 'bg-green-500' :
                trackingScore >= 70 ? 'bg-green-500/80' :
                trackingScore >= 50 ? 'bg-yellow-500' :
                'bg-red-500'
              }`}
              style={{ width: `${trackingScore}%` }}
            />
          </div>
          <span className={`text-xs font-medium tabular-nums ${getScoreColor(trackingScore)}`}>
            {tracked}/{total}
          </span>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-border/40 divide-y divide-border/20 py-1">
          {trackingAnalysis.elements.map((element: any, i: number) => (
            <ElementRow key={i} element={element} />
          ))}
        </div>
      )}
    </div>
  );
}

// Keep ElementCard export for backwards compatibility
export function ElementCard({ element }: { element: any }) {
  return <ElementRow element={element} />;
}
